import React, { useState } from 'react'
import '../../assets/styles/feedback.css';
import commonService from '../../services/otherServices';
import Popup from '../../components/Common/Error';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface FeedbackProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FeedbackForm {
  rating: number;
  features: string[];
  comments: string;
  recommend: boolean;
}

interface FormErrors {
  rating?: string;
  features?: string;
}

const Feedback: React.FC<FeedbackProps> = ({ isOpen, onClose, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [comments, setComments] = useState("");
  const [recommend, setRecommend] = useState(false);
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Feature options
  const features = [
    { id: 'emailVerification', label: 'Email Verification' },
    { id: 'customDataRequest', label: 'Custom Data Request' },
    { id: 'dataEnrichment', label: 'Data Enrichment' }
  ];

  // Handle feature selection
  const handleFeatureChange = (featureId: string) => {
    setSelectedFeatures(prev => {
      if (prev.includes(featureId)) {
        return prev.filter(id => id !== featureId);
      } else {
        return [...prev, featureId];
      }
    });

    // Clear feature error when user selects something
    if (formErrors.features) {
      setFormErrors(prev => ({ ...prev, features: '' }));
    }
  };

  // Handle rating change
  const handleRatingChange = (star: number) => {
    setRating(star);

    // Clear rating error when user selects something
    if (formErrors.rating) {
      setFormErrors(prev => ({ ...prev, rating: '' }));
    }
  };

  // Validation function
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    // Rating validation
    if (rating === 0) {
      errors.rating = 'Please rate your experience';
    } else if (rating < 1 || rating > 5) {
      errors.rating = 'Rating must be between 1 and 5';
    }

    // Features validation
    if (selectedFeatures.length === 0) {
      errors.features = 'Please select at least one feature';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Reset form
  const resetForm = () => {
    setRating(0);
    setSelectedFeatures([]);
    setComments('');
    setRecommend(false);
    setFormErrors({});
  };

  // Handle form submission
  const onSubmit = async () => {
    // Validate form
    if (!validateForm()) {
      toast.error('Please complete all required fields');
      return;
    }

    try {
      setIsLoading(true);

      // Create FormData
      const formData = new FormData();
      formData.append('rating', rating.toString());
      formData.append('feature', selectedFeatures.join(','));
      formData.append('comments', comments.trim() || '');
      formData.append('recommend', recommend.toString());

      const response = await commonService.feedback(formData);

      if (response && response?.data?.status === false) {
        setErrorMsg(response.data.message || 'Failed to submit feedback');
        return;
      }


      // Reset form
      resetForm();

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close popup after a delay
      // setTimeout(() => {
        onClose();
      // }, 5000);

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Something went wrong';
      setErrorMsg(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle close with reset
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <>
      <div className="feedback-overlay">
        <div className="feedback-popup">
          {/* Header */}
          <div className="d-flex justify-content-end">
            <div>
              <img
                src="/images/grey-cancel-cta.png"
                alt="close"
                className="feedback-close"
                onClick={handleClose}
              />
            </div>
          </div>

          <div className="d-flex justify-content-center mb-3">
            <div>
              <img src="/images/corecrate-logo.png" alt="corecrate Logo" style={{height:"27px"}} />
            </div>
          </div>

          {/* Body */}
          <div className="feedback-body">
            <p className="feedback-title">We'd Love Your Input!</p>
            <label className="feedback-label">
              Your feedback helps us enhance your Spokesly experience.
            </label>

            {/* Rating Section */}
            <p className="seamless">How seamless was your experience in building your database?</p>

            <div className="rating-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <img
                  key={star}
                  src={star <= (hover || rating) ? "/images/blue_star.png" : "/images/star.png"}
                  alt={`${star} star`}
                  className={`star ${star <= (hover || rating) ? "active" : ""}`}
                  onClick={() => handleRatingChange(star)}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                />
              ))}
            </div>
            {formErrors.rating && (
              <div className="error-message text-center mt-1">{formErrors.rating}</div>
            )}

            {/* Features Section */}
            <p className="seamless mt-4">Which feature do you find most useful?</p>

            <div className="checkbox-container">
              <div className="checkbox-wrapper">
                <input
                  type="checkbox"
                  id="emailVerification"
                  checked={selectedFeatures.includes('emailVerification')}
                  onChange={() => handleFeatureChange('emailVerification')}
                />
                <label htmlFor="emailVerification">Email Verification</label>
              </div>

              <div className="checkbox-wrapper">
                <input
                  type="checkbox"
                  id="customDataRequest"
                  checked={selectedFeatures.includes('customDataRequest')}
                  onChange={() => handleFeatureChange('customDataRequest')}
                />
                <label htmlFor="customDataRequest">Custom Data Request</label>
              </div>

              <div className="checkbox-wrapper">
                <input
                  type="checkbox"
                  id="dataEnrichment"
                  checked={selectedFeatures.includes('dataEnrichment')}
                  onChange={() => handleFeatureChange('dataEnrichment')}
                />
                <label htmlFor="dataEnrichment">Data Enrichment</label>
              </div>
            </div>
            {formErrors.features && (
              <div className="error-message text-center mt-1">{formErrors.features}</div>
            )}

            {/* Comments Section */}
            <p className="seamless mt-2">Any thoughts or ideas for improvement?</p>

            <div className="feedback-body text-center">
              <textarea
                className="feedback-textarea"
                // placeholder="Tell us what you think..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                maxLength={1000}
              />
            </div>

            {/* Recommendation Section */}
            <div className="checkbox-wrapper mt-3 justify-content-center">
              <input
                type="checkbox"
                id="customCheck"
                checked={recommend}
                style={{border:"1.5px solid #a5a3a3"}}
                onChange={(e) => setRecommend(e.target.checked)}
              />
              <label htmlFor="customCheck">Would you recommend Spokesly to others?</label>
            </div>
          </div>


          {/* Footer */}
          <div className="feedback-footer">
            <button
              className="feedback-submit"
              onClick={onSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </button>
          </div>
        </div>
      </div>


      
      <Popup
        isOpen={!!errorMsg}
        onClose={() => setErrorMsg("")}
        title={errorMsg}
        iconSrc="/images/grey-cancel-cta.png"
      />
    </>
  );
};

export default Feedback;